import React, { useEffect, useState, useMemo } from 'react';
import { Box, Grid, Card, Typography, Avatar, Table, TableBody, TableCell, TableHead, TableRow, Paper, Container, TablePagination, TextField, CircularProgress, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Logout } from '@mui/icons-material'; // Logout import is fine, but unused here
import { getUserInfo } from '../../hooks/apiHandler'; // Mock API function to fetch user info

// Helper to format date consistent with the report style
const formatLogDate = (date) => {
    return `${String(date.getDate()).padStart(2, '0')}-${date.toLocaleString('en-US', { month: 'short' })}-${String(date.getFullYear()).slice(-2)}`;
};

// Helper to format time (e.g., 09:00 AM)
const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

// Data generation and processing utility
export function generateGraphData(timePunch) {
    const dailyTotals = {};
    const timeTable = [];
    let totalHoursWorked = 0;

    timePunch.forEach(punch => {
        const start = new Date(punch.startTime);
        // Handle "Clocked In" status gracefully
        const isClockedIn = !punch.endTime;
        const end = isClockedIn ? new Date() : new Date(punch.endTime);
        
        // Calculate duration and handle possible negative duration if data is bad
        const durationMs = Math.max(0, end - start);
        const durationHours = durationMs / (1000 * 60 * 60); // hours

        const graphKey = start.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }).replace(',', '');
        const dateKey = formatLogDate(start);

        const login = formatTime(start);
        const logout = isClockedIn ? "Clocked In" : formatTime(end);

        timeTable.push({
            date: dateKey,
            login,
            logout,
            hours: +durationHours.toFixed(2),
            isClockedIn
        });

        dailyTotals[graphKey] = (dailyTotals[graphKey] || 0) + durationHours;
        totalHoursWorked += durationHours;
    });

    const graphData = Object.entries(dailyTotals).map(([date, hours]) => ({
        date,
        hours: +hours.toFixed(2)
    }));

    return { graphData, timeTable, totalHoursWorked: +totalHoursWorked.toFixed(2) };
}


export default function UserInfoScreen() {
    const theme = useTheme();
    const [userData, setUserData] = useState(null);
    const [graphData, setGraphData] = useState([]);
    const [timeLogs, setTimeLogs] = useState([]);
    const [totalHours, setTotalHours] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterDate, setFilterDate] = useState("");
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        const fetchDashData = async () => {
            setLoading(true);
            try {
                // Using the mock getUserInfo function now
                const resp = await getUserInfo(); 
                if (resp.code === 0 && resp.data) {
                    const result = generateGraphData(resp.data.timePunch || []);
                    
                    setUserData(resp.data);
                    setGraphData(result.graphData);
                    setTimeLogs(result.timeTable);
                    setTotalHours(result.totalHoursWorked);
                } else {
                    console.error("Failed to fetch user data:", resp);
                }
            } catch (error) {
                console.error("API error:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDashData();
    }, []);


    // Convert log.date (e.g., "21-Apr-25") into yyyy-mm-dd for comparison
    const convertLogDateToISO = (logDate) => {
        const [day, monthStr, year] = logDate.split("-");
        const months = {
            Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
            Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
        };
        const month = months[monthStr];
        // Safely assume 20xx year format
        return `20${year}-${month}-${day}`; 
    };

    const filteredLogs = useMemo(() => {
        return filterDate
            ? timeLogs.filter(log => convertLogDateToISO(log.date) === filterDate)
            : timeLogs;
    }, [timeLogs, filterDate]);
    
    // Paginated logs
    const paginatedLogs = filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    if (loading || userData === null) {
        return (
            <Container sx={{ py: 5, textAlign: 'center' }}>
                <CircularProgress color="primary" />
                <Typography sx={{ mt: 2 }}>Loading user data...</Typography>
            </Container>
        );
    }

    const joinDate = userData.userDate ? formatLogDate(new Date(userData.userDate)) : 'N/A';

    return (
        <Box sx={{ flexGrow: 1, p: 4, background: theme.palette.grey[50], minHeight: '100vh' }}>
            <Grid container spacing={4}>

                {/* --- 1. User Info & Key Stats Card --- */}
                <Grid item xs={12}>
                    <Card elevation={4} sx={{ p: 4, background: theme.palette.primary.main, color: '#fff' }}>
                        <Grid container spacing={3} alignItems="center">
                            {/* Avatar & Name */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: theme.palette.secondary.main }}>
                                        {userData.name ? userData.name[0].toUpperCase() : 'U'}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{userData.name}</Typography>
                                        <Typography variant="body2">{userData.userID}</Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Contact Info */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Contact Info</Typography>
                                <Typography variant="body1">{userData.email}</Typography>
                                <Typography variant="body1">{userData.phone}</Typography>
                            </Grid>
                            
                            {/* Total Hours Worked */}
                            <Grid item xs={12} sm={6} md={3} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Hours Tracked</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{totalHours}h</Typography>
                            </Grid>

                            {/* Join Date */}
                            <Grid item xs={12} sm={6} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Member Since</Typography>
                                <Typography variant="h6">{joinDate}</Typography>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                {/* --- 2. Weekly Activity Graph --- */}
                <Grid item xs={12}>
                    <Card elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.dark }}>
                            Time Log Trend
                        </Typography>
                        <ResponsiveContainer width="100%" height={350}>
                            <LineChart
                                data={graphData}
                                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
                                <YAxis 
                                    label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: theme.palette.text.secondary }}
                                    stroke={theme.palette.text.secondary}
                                />
                                <Tooltip formatter={(value) => [`${value} hours`, 'Time']} />
                                <Line 
                                    type="monotone" 
                                    dataKey="hours" 
                                    stroke={theme.palette.secondary.main} 
                                    strokeWidth={3} 
                                    activeDot={{ r: 8, fill: theme.palette.primary.light }} 
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </Grid>

                {/* --- 3. Detailed Time Logs Table --- */}
                <Grid item xs={12}>
                    <Card elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3, color: theme.palette.primary.dark }}>
                            Detailed Time Logs
                        </Typography>

                        {/* Date Filter & Table */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {filteredLogs.length} total entries found
                            </Typography>
                            <TextField
                                type="date"
                                label="Filter by Date"
                                variant="outlined"
                                size="small"
                                value={filterDate}
                                onChange={(e) => { setFilterDate(e.target.value); setPage(0); }}
                                sx={{ width: '180px' }}
                            />
                        </Box>

                        <Paper variant="outlined" sx={{ width: '100%', overflow: 'auto', border: `1px solid ${theme.palette.divider}` }}>
                            <Table size="medium" sx={{ minWidth: 650 }}>
                                <TableHead sx={{ backgroundColor: theme.palette.primary.light }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.common.white }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.common.white }}>Clock-in Time</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.common.white }}>Clock-out Time</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.common.white, textAlign: 'right' }}>Hours Worked</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedLogs.map((log, index) => (
                                        <TableRow 
                                            key={index} 
                                            sx={{ 
                                                '&:nth-of-type(odd)': { backgroundColor: theme.palette.grey[100] },
                                                '&:hover': { backgroundColor: theme.palette.grey[200] }
                                            }}
                                        >
                                            <TableCell>{log.date}</TableCell>
                                            <TableCell>{log.login}</TableCell>
                                            <TableCell 
                                                sx={{ 
                                                    color: log.isClockedIn ? theme.palette.error.main : theme.palette.text.primary,
                                                    fontWeight: log.isClockedIn ? 'bold' : 'normal'
                                                }}
                                            >
                                                {log.logout}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', fontWeight: 'medium' }}>
                                                {log.hours}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={filteredLogs.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25]}
                            />
                        </Paper>
                    </Card>
                </Grid>

            </Grid>
        </Box>
    );
}
